import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { Select } from 'shared/ui/Select/Select'
import { Button, EnumDropdownItemVariant, type IDropdownItem } from 'shared/ui'
import type { IFilterValue } from 'entities/Filters/types'
import type { IResponseFilter } from 'entities/Contacts/api/filterTypes'
import { getConfigByStatus, TriggerStatus } from 'entities/Trigger'
import { type ITriggerStatus } from 'entities/Trigger/api/type'
import type { IWidgetProps } from 'widgets/FilterEditor/types/types'

import { WidgetTriggerStatusTrigger } from './WidgetTriggerStatusTrigger'
import styles from './styles.module.scss'

export const WidgetTriggerStatus = observer(
  ({ filterItem, expandByDefault = false, parentsDropdownId }: IWidgetProps) => {
    const [showDropdown, setShowDropdown] = useState(false)
    const [showValuesWarning, setShowValuesWarning] = useState(false)
    const filterConfig = filterItem.config as IResponseFilter<IFilterValue>
    const values = filterConfig.values ?? []
    const payloadValue = useMemo(
      () =>
        Array.isArray(filterItem.payload.value)
          ? (filterItem.payload.value as ITriggerStatus[])
          : [],
      [filterItem.payload.value]
    )
    const [primaryKeys, setPrimaryKeys] = useState<ITriggerStatus[]>([])
    const [selectedKeys, setSelectedKeys] = useState<ITriggerStatus[]>(payloadValue)
    const dropdownItems: IDropdownItem[] = useMemo(
      () =>
        values
          .reduce<[IFilterValue[], IFilterValue[]]>(
            (values, value) => {
              const isSelected = primaryKeys.includes(value.key as ITriggerStatus)

              if (isSelected) values[0].push(value)
              else values[1].push(value)

              return values
            },
            [[], []]
          )
          .flat()
          .map((item, index) => {
            const key = item.key as ITriggerStatus

            return {
              id: key,
              label: getConfigByStatus(key).title,
              labelContent: <TriggerStatus status={key} />,
              variant: EnumDropdownItemVariant.Checkbox,
              activeValue: selectedKeys.includes(key),
              divider: index === primaryKeys.length - 1 && primaryKeys.length !== values.length,
            }
          }),
      [selectedKeys, primaryKeys]
    )

    useEffect(() => {
      setPrimaryKeys(payloadValue)
    }, [])

    useEffect(() => {
      setTimeout(() => setShowDropdown(expandByDefault), 100)
    }, [expandByDefault])

    return (
      <Select
        text={filterItem.operator?.key ?? ''}
        type='multi'
        items={dropdownItems}
        dropdownProps={{
          ariaLabel: 'Select',
          parentsDropdownId: parentsDropdownId,
          show: showDropdown,
          textFieldProps: {
            InputProps: { placeholder: `Search ${filterItem.config.name}` },
          },
          bottomActions: selectedKeys.length
            ? () => (
                <Button
                  text='Clear'
                  contained='secondary'
                  typeBtn='text'
                  onClick={() => {
                    setPrimaryKeys([])
                    setSelectedKeys([])
                    filterItem.payload.updateHard({ value: [] })
                  }}
                />
              )
            : undefined,
          withSearch: true,
          onClose: () => {
            setPrimaryKeys(selectedKeys)
            setShowValuesWarning(!selectedKeys.length)
          },
          triggerComponent: () => (
            <div className={classNames({ [styles.warning]: showValuesWarning })}>
              <WidgetTriggerStatusTrigger items={selectedKeys} />
            </div>
          ),
          onChange: (item) => {
            const keys = (
              item.activeValue
                ? selectedKeys.filter((key) => key !== item.id)
                : [...selectedKeys, item.id]
            ) as ITriggerStatus[]

            setShowValuesWarning(false)
            setSelectedKeys(keys)
            filterItem.payload.updateHard({ value: keys })
          },
        }}
      />
    )
  }
)
